import React, { useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { Check, Info, MoreVertical, Pencil, X } from 'lucide-react';
import { isAxiosError } from 'axios';
import { useImmutableCallback } from '@agro1desenvolvimento/react-hooks';
import { keysToSnakeCaseDeep } from '@agro1desenvolvimento/utils-js';
import {
  Aplicacao,
  AuditoriaAplicacaoObjeto,
  TypedAplication,
} from '../../@types';
import AuditoriaAplicacoesService from '../../services/auditoria-aplicacoes';
import aproveToast from './aproveToast';
import { formatStringDate } from '../utils';
import usePendingApprovesStore from './store';
import PendingAproveItemModal from './PendingAproveItemModal';
import PendingAproveDeleteConfirmModal from './PendingAproveDeleteConfirmModal';
import DeleteRecomendacaoModal from './DeleteRecomendacaoModal';
import {
  AqilaRecomendacoesService,
  RecomendacoesService,
} from '../../services';
import Agro1Toast from '../../components/Agro1Toast';
import PendingAproveTooltipService from './pending-aprove-tooltip.service';
import { buildPendingAproveMainObject } from './pending-aprove-main-object.builder';
import PermissoesService from '../../services/permissoes';

const AQILA_OPERACAO_SIDEBAR: Record<
  number,
  { event: string; activeTab: string }
> = {
  3: { event: 'addRecommendationAdubacao', activeTab: 'adubacao' },
  11: { event: 'addRecommendationCorrecao', activeTab: 'correcaoSolo' },
};

const DEFAULT_AQILA_SIDEBAR = {
  event: 'addRecommendation',
  activeTab: 'recomendacao',
};

interface PendingAproveItemProps {
  item: TypedAplication;
}

const PendingAproveItem: React.FC<PendingAproveItemProps> = ({ item }) => {
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isActionModalVisible, setIsActionModalVisible] = useState(false);
  const [
    isDeleteRecomendacaoModalVisible,
    setIsDeleteRecomendacaoModalVisible,
  ] = useState(false);
  const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = useState(false);
  const updateItemStatusProcessando = usePendingApprovesStore(
    (state) => state.updateItemStatusProcessando,
  );
  const refreshAproves = usePendingApprovesStore(
    (state) => state.refreshAproves,
  );
  const usuariosPorPropriedade = usePendingApprovesStore(
    (state) => state.usuariosPorPropriedade,
  );

  const pendingAproveMainObject = useMemo(
    () => buildPendingAproveMainObject(item),
    [item],
  );

  const editUrl = useMemo(() => {
    const { propriedadeId } = pendingAproveMainObject;

    if (item.type === 'aqilaRecomendacoes') {
      const { event, activeTab } =
        AQILA_OPERACAO_SIDEBAR[item.operacao.id] ?? DEFAULT_AQILA_SIDEBAR;
      return `/#!/propriedade?sidebar=${event}&propriedade=${propriedadeId}&activeRecommendationsTab=${activeTab}`;
    }

    if (item.type === 'recomendacoes') {
      return `/#!/propriedade?sidebar=addRecommendation&propriedade=${propriedadeId}`;
    }

    return `/#!/propriedade?sidebar=edicaoAuditoriaAplicacao&propriedade=${propriedadeId}&activeAuditoriaAplicacao=${item.id}`;
  }, [item, pendingAproveMainObject]);

  const handleEditClick = useImmutableCallback(() => {
    let event = 'edicaoAuditoriaAplicacao';

    if (item.type === 'aqilaRecomendacoes') {
      event = (
        AQILA_OPERACAO_SIDEBAR[item.operacao.id] ?? DEFAULT_AQILA_SIDEBAR
      ).event;
    } else if (item.type === 'recomendacoes') {
      event = 'addRecommendation';
    }

    window.localStorage.setItem(
      'edit_redirect_data',
      JSON.stringify({
        event,
        rightSide: event,
        params: keysToSnakeCaseDeep(item),
      }),
    );
  });

  const handleOpenActionModal = useImmutableCallback(() => {
    setIsActionModalVisible(true);
  });

  const handleDeleteRecomendacaoModal = useImmutableCallback(() => {
    setIsDeleteRecomendacaoModalVisible(true);
  });

  const handleCloseActionModal = useImmutableCallback(() => {
    setIsActionModalVisible(false);
  });

  const handleVerifyExistingApplications = useImmutableCallback(() => {
    Agro1Toast.show({
      detail: 'Não implementado.',
      severity: 'error',
      sticky: false,
    });
    handleCloseActionModal();
  });

  const handleOpenDeleteConfirm = useImmutableCallback(() => {
    setIsDeleteConfirmVisible(true);
  });

  const handleCloseDeleteRecomendacaoModal = useImmutableCallback(() => {
    setIsDeleteRecomendacaoModalVisible(false);
  });

  const handleCloseDeleteConfirm = useImmutableCallback(() => {
    setIsDeleteConfirmVisible(false);
  });

  const handleApprove = useImmutableCallback(async () => {
    setIsApproving(true);

    try {
      if (pendingAproveMainObject.type === 'recomendacoes') {
        await RecomendacoesService.aprovar(item.id);

        refreshAproves();
      } else if (pendingAproveMainObject.type === 'aqilaRecomendacoes') {
        await AqilaRecomendacoesService.aprovar(item.id);

        refreshAproves();
      } else {
        await AuditoriaAplicacoesService.update(item.id, 'aprovado');
      }

      updateItemStatusProcessando(item.id, item.type, 'R');
    } catch (error) {
      console.error(error);

      const entidade =
        pendingAproveMainObject.type === 'aplicacoes'
          ? 'aplicação'
          : 'recomendação';

      if (isAxiosError(error)) {
        const data = error.response?.data;
        const apiMessage =
          data?.message || (typeof data === 'string' ? data : null);

        aproveToast.error(
          apiMessage
            ? `Falha ao aprovar ${entidade}: ${apiMessage}`
            : `Falha ao aprovar ${entidade}, tente novamente.`,
        );
      } else {
        aproveToast.error(`Falha ao aprovar ${entidade}, tente novamente.`);
      }
    } finally {
      setIsApproving(false);
    }
  });

  const shouldShowProcessingTag =
    pendingAproveMainObject.statusProcessando === 'R';

  const parsedObjeto = useMemo<AuditoriaAplicacaoObjeto>(() => {
    if (item.type !== 'aplicacoes') return null;

    const { objeto } = item.auditoriaAplicacao;

    if (typeof objeto === 'string') {
      try {
        return JSON.parse(objeto);
      } catch {
        return {};
      }
    }

    return objeto || {};
  }, [item]);

  const handleConfirmDeletePendingApplication = useImmutableCallback(
    async () => {
      setIsRejecting(true);

      try {
        await AuditoriaAplicacoesService.update(item.id, 'rejeitado');

        await refreshAproves();

        handleCloseDeleteConfirm();
        handleCloseActionModal();

        aproveToast.rejectSuccess();
      } catch (error) {
        console.error(error);
        aproveToast.rejectError();
      } finally {
        setIsRejecting(false);
      }
    },
  );

  const infoTooltipContent = useMemo(
    () =>
      PendingAproveTooltipService.buildTooltipContent(
        pendingAproveMainObject,
        item,
        parsedObjeto,
        usuariosPorPropriedade,
      ),
    [pendingAproveMainObject, item, parsedObjeto, usuariosPorPropriedade],
  );

  const infoButtonId = `pending-aprove-info-${item.type}-${item.id}`;

  return (
    <div className="panel-list-item">
      <div className="pending-aprove-item-propriedade">
        <span className="pending-aprove-item-propriedade-nome">
          {pendingAproveMainObject.propriedade}
        </span>
        {pendingAproveMainObject.gleba && (
          <span className="pending-aprove-item-propriedade-gleba">
            {pendingAproveMainObject.gleba}
          </span>
        )}
        <span className="pending-aprove-item-propriedade-usuario">
          {pendingAproveMainObject.usuario}
        </span>
      </div>

      <div className="panel-list-item-center">
        <div className="panel-list-item-title-row">
          <div className="panel-list-item-title pending-aprove-item-title-with-info">
            <span>{pendingAproveMainObject.tipoOperacao}</span>
            <Button
              id={infoButtonId}
              icon={<Info size={16} />}
              className="p-button-rounded pending-aprove-item-btn pending-aprove-item-btn-info"
              disabled={isApproving}
            />
            <Tooltip
              target={`#${infoButtonId}`}
              position="top"
              className="pending-aprove-item-info-tooltip"
            >
              {infoTooltipContent}
            </Tooltip>
          </div>
        </div>
        <div className="panel-list-item-date">
          {formatStringDate(
            pendingAproveMainObject.type === 'aplicacoes'
              ? pendingAproveMainObject.dataInicial
              : item.createdAt,
          )}
        </div>
      </div>

      <div className="panel-list-item-actions">
        {shouldShowProcessingTag ? (
          <span className="pending-aprove-item-status-tag">PROCESSANDO</span>
        ) : (
          <>
            {!pendingAproveMainObject.isRecomendacao &&
              PermissoesService.users.canAuditPulverization && (
                <>
                  <Button
                    icon={<MoreVertical size={14} />}
                    className="p-button-rounded pending-aprove-item-btn pending-aprove-item-btn-ellipsis"
                    disabled={isApproving || isRejecting}
                    onClick={handleOpenActionModal}
                  />
                  <Button
                    icon={<Check size={14} />}
                    className="p-button-rounded pending-aprove-item-btn pending-aprove-item-btn-confirm"
                    disabled={isApproving || isRejecting}
                    loading={isApproving}
                    onClick={handleApprove}
                  />
                  <a
                    href={editUrl}
                    onClick={(e) => {
                      if (isApproving || isRejecting) {
                        e.preventDefault();
                        return;
                      }
                      handleEditClick();
                    }}
                  >
                    <Button
                      icon={<Pencil size={14} />}
                      className="p-button-rounded pending-aprove-item-btn"
                      disabled={isApproving || isRejecting}
                    />
                  </a>
                </>
              )}
            {pendingAproveMainObject.isRecomendacao &&
              PermissoesService.users.canAproveRecommendation && (
                <>
                  <Button
                    icon={<X size={14} />}
                    className="p-button-rounded pending-aprove-item-btn pending-aprove-item-btn-reject"
                    disabled={isApproving || isRejecting}
                    onClick={handleDeleteRecomendacaoModal}
                  />
                  <Button
                    icon={<Check size={14} />}
                    className="p-button-rounded pending-aprove-item-btn pending-aprove-item-btn-confirm"
                    disabled={isApproving || isRejecting}
                    loading={isApproving}
                    onClick={handleApprove}
                  />
                  <a
                    href={editUrl}
                    onClick={(e) => {
                      if (isApproving || isRejecting) {
                        e.preventDefault();
                        return;
                      }
                      handleEditClick();
                    }}
                  >
                    <Button
                      icon={<Pencil size={14} />}
                      className="p-button-rounded pending-aprove-item-btn"
                      disabled={isApproving || isRejecting}
                    />
                  </a>
                </>
              )}
          </>
        )}
      </div>
      {!pendingAproveMainObject.isRecomendacao ? (
        <>
          <PendingAproveItemModal
            visible={isActionModalVisible}
            item={item as Extract<TypedAplication, Aplicacao>}
            onVerifyExistingApplications={handleVerifyExistingApplications}
            onDeletePendingApplication={handleOpenDeleteConfirm}
            onHide={handleCloseActionModal}
          />

          <PendingAproveDeleteConfirmModal
            visible={isDeleteConfirmVisible}
            isLoading={isRejecting}
            onConfirm={handleConfirmDeletePendingApplication}
            onHide={handleCloseDeleteConfirm}
          />
        </>
      ) : (
        <DeleteRecomendacaoModal
          visible={isDeleteRecomendacaoModalVisible}
          id={item.id}
          aqilaRecomendacao={
            pendingAproveMainObject.type === 'aqilaRecomendacoes'
          }
          onHide={handleCloseDeleteRecomendacaoModal}
        />
      )}
    </div>
  );
};

export default React.memo(PendingAproveItem);
